// Type only, so loading this file never pulls in the card components (and Fluent with them).
import type { Card } from "../CardFactory";
import { PiAsset, findAssetByCapabillityGuid } from "../Types/PiAsset";
import { getAssetCapabilities } from "./AssetTreeShared";

export const DASHBOARD_ASSET_TREE_STORAGE_KEY = "dashboardAssetTreeExpandedItems";

/** Capabilities that have a dashboard card, with their menu labels. */
export const DASHBOARD_CAPABILITY_LABELS: { [name: string]: string } = {
    oee: "OEE",
    mstatus: "Machine status",
    machine: "Machine view",
};

export const NOT_IMPLEMENTED_LABEL = "Not implemented in PDP2";

/** Saved expanded-state id: the parent's name and the asset's name. Kept as it was so saved trees still open. */
export const dashboardAssetId = (asset: PiAsset, parent: PiAsset | undefined) =>
    parent ? `${parent.Name}/${asset.Name}` : asset.Name;

/** Title of a card added from the drawer. */
export const dashboardCardName = (asset: PiAsset) => asset.WorkCenter + " " + asset.Name + " " + asset.Alias;

export interface DashboardAssetAction {
    label: string;
    disabled?: boolean;
    add?: { type: string; elementGuid: string; name: string };
    remove?: { eguid: string; type: string };
}

/**
 * Menu of an asset in the drawer: add or remove a card per capability that has one. A card of a capability without a
 * card type can still be removed. With nothing to offer it holds a single disabled "Not implemented in PDP2" entry.
 */
export function getDashboardAssetActions(asset: PiAsset, cards: Card[]): DashboardAssetAction[] {
    const actions: DashboardAssetAction[] = [];

    for (const capability of getAssetCapabilities(asset)) {
        const label = DASHBOARD_CAPABILITY_LABELS[capability.Name];
        const isOnDashboard = cards.some((card) => card.elementGuid === capability.Guid && card.type === capability.Name);

        if (isOnDashboard) {
            actions.push({ label: `Remove ${label ?? capability.Name}`, remove: { eguid: capability.Guid, type: capability.Name } });
        } else if (label) {
            actions.push({ label, add: { type: capability.Name, elementGuid: capability.Guid, name: dashboardCardName(asset) } });
        }
    }

    return actions.length > 0 ? actions : [{ label: NOT_IMPLEMENTED_LABEL, disabled: true }];
}

/** Assets with a card on the dashboard, most recently added first, each once. */
export function getPinnedAssets(root: PiAsset, cards: Card[]): PiAsset[] {
    const pinned: PiAsset[] = [];

    for (const card of cards) {
        const asset = findAssetByCapabillityGuid(root, card.elementGuid);
        if (asset && !pinned.some((other) => other.Guid === asset.Guid)) {
            pinned.push(asset);
        }
    }

    return pinned.reverse();
}
